import React, { useEffect, useState } from 'react';
import { Button } from '@/components';
import { SideBarModal } from '@/components/Modal';
import { ButtonVariant } from '@/types/common';

interface ReasonConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  isSubmitting?: boolean;
  title: string;
  description: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  submitLabel: string;
  submitVariant?: ButtonVariant;
}

/**
 * Shared shape for the two outstanding actions that only need a recorded
 * reason: Release (write-off) and Override (allow a new rental). Same
 * anatomy as RejectPurchaseOrderModal — one required textarea, submit
 * disabled until it has content.
 */
const ReasonConfirmModal: React.FC<ReasonConfirmModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  title,
  description,
  reasonLabel,
  reasonPlaceholder,
  submitLabel,
  submitVariant = 'primary',
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError(`${reasonLabel} is required`);
      return;
    }
    onSubmit(reason.trim());
  };

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="center"
      width="md"
      maxHeight="auto"
      header={
        <div>
          <div className="text-base font-semibold text-gray-900">{title}</div>
          <div className="text-xs text-gray-500">{description}</div>
        </div>
      }
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant={submitVariant}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : submitLabel}
          </Button>
        </div>
      }
      className="max-w-[520px]"
    >
      <div className="px-6 py-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {reasonLabel} <span className="text-red-500">*</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError('');
          }}
          rows={4}
          placeholder={reasonPlaceholder}
          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    </SideBarModal>
  );
};

export default ReasonConfirmModal;
